import { useMemo, useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Card,
  Table,
  Form,
  Alert,
  Spinner,
} from "react-bootstrap";

import {
  Search,
  RefreshCw,
  Filter,
  AlertCircle,
  PackageSearch,
  X,
} from "lucide-react";

import NavbarUtility from "./NavbarUtility";

// ==========================================================
// JSON LOCALE
// ==========================================================

const BACKORDER_JSON_URL = "/backorder.json";

const oggi = new Date().toISOString().split("T")[0];

// ==========================================================
// COLORI
// ==========================================================

const BG_PAGE = "#181b21";
const PANEL_BG = "rgba(43, 47, 56, 0.78)";
const FILTER_BOX_BG = "rgba(255, 255, 255, 0.045)";
const FILTER_BOX_BORDER = "rgba(235, 181, 27, 0.24)";
const GOLD = "#ebb51b";
const GOLD_DARK = "#a67a10";
const TEXT_DARK = "#f1f1ee";
const TEXT_MUTED = "#a9aaad";

// ==========================================================
// COMPONENTE
// ==========================================================

const BackOrder = ({
  embedded = false,
  initialClientId = "",
  initialRagioneSociale = "",
}) => {
  const [searchParams] = useSearchParams();

  const clienteRef = useRef(null);

  // ========================================================
  // JSON COMPLETO
  // ========================================================

  const [allBackorders, setAllBackorders] = useState([]);
  const [loadingJson, setLoadingJson] = useState(true);

  // ========================================================
  // CLIENTE
  // ========================================================

  const [idCliente, setIdCliente] = useState(
    String(initialClientId || ""),
  );

  const [ragioneSociale, setRagioneSociale] = useState(
    initialRagioneSociale || "",
  );

  const [clienteSearch, setClienteSearch] = useState(
    initialClientId
      ? initialRagioneSociale
        ? `${initialRagioneSociale} (${initialClientId})`
        : String(initialClientId)
      : "",
  );

  const [clienti, setClienti] = useState([]);
  const [showClienti, setShowClienti] = useState(false);

  // ========================================================
  // DATE
  // ========================================================

  const [dataDa, setDataDa] = useState("2026-01-01");
  const [dataA, setDataA] = useState(oggi);

  // ========================================================
  // BACKORDER CLIENTE
  // ========================================================

  const [backorders, setBackorders] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);

  // ========================================================
  // FUNZIONE LETTURA CODICE CLIENTE
  // Gestisce sia "ID Cliente" sia "ID cliente"
  // ========================================================

  const getCodiceCliente = (item) => {
    return String(
      item?.["ID Cliente"] ??
        item?.["ID cliente"] ??
        "",
    ).trim();
  };

  // ========================================================
  // FUNZIONE LETTURA RAGIONE SOCIALE
  // ========================================================

  const getRagioneSociale = (item) => {
    return String(
      item?.["Ragione Sociale"] ?? "",
    ).trim();
  };

  // ========================================================
  // CONVERSIONE DATA DATABASE -> YYYY-MM-DD
  // ========================================================

  const normalizzaData = (value) => {
    if (!value) {
      return "";
    }

    const valore = String(value).trim();

    const match = valore.match(
      /^(\d{4})-(\d{2})-(\d{2})/,
    );

    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }

    return "";
  };

  // ========================================================
  // CARICAMENTO BACKORDER.JSON
  // ========================================================

  useEffect(() => {
    const caricaBackorderJson = async () => {
      setLoadingJson(true);
      setError("");

      try {
        /*
         * Timestamp per evitare che il browser utilizzi
         * una vecchia versione del JSON dalla cache.
         */
        const response = await fetch(
          `${BACKORDER_JSON_URL}?t=${Date.now()}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            cache: "no-store",
          },
        );

        if (!response.ok) {
          throw new Error(
            `Errore caricamento JSON: HTTP ${response.status}`,
          );
        }

        const data = await response.json();

        const rows = Array.isArray(data?.BACKORDER_JSON)
          ? data.BACKORDER_JSON.flat()
          : [];

        console.log(
          "BACKORDER JSON CARICATO:",
          rows.length,
        );

        setAllBackorders(rows);
      } catch (err) {
        console.error(
          "Errore caricamento backorder.json:",
          err,
        );

        setAllBackorders([]);

        setError(
          err.message ||
            "Impossibile caricare backorder.json.",
        );
      } finally {
        setLoadingJson(false);
      }
    };

    caricaBackorderJson();
  }, []);

  // ========================================================
  // ELENCO UNICO CLIENTI PRESENTI NEL JSON
  // ========================================================

  const clientiDisponibili = useMemo(() => {
    const clientiMap = new Map();

    allBackorders.forEach((item) => {
      const codice = getCodiceCliente(item);
      const ragione = getRagioneSociale(item);

      if (!codice || !ragione) {
        return;
      }

      if (!clientiMap.has(codice)) {
        clientiMap.set(codice, {
          codice_cliente: codice,
          ragione_sociale: ragione,
          zona: item?.Zona || "",
          nazione: item?.Nazione || "",
          operatore: item?.Operatore || "",
          agente: item?.Agente || "",
        });
      }
    });

    return Array.from(clientiMap.values());
  }, [allBackorders]);

  // ========================================================
  // AUTOCOMPLETE CLIENTE LOCALE
  // NESSUNA FETCH
  // ========================================================

  useEffect(() => {
    if (embedded) {
      setClienti([]);
      setShowClienti(false);
      return;
    }

    const valore = clienteSearch
      .trim()
      .toLowerCase();

    if (valore.length < 2) {
      setClienti([]);
      setShowClienti(false);
      return;
    }

    if (!clientiDisponibili.length) {
      setClienti([]);
      setShowClienti(false);
      return;
    }

    const risultati = clientiDisponibili
      .filter((cliente) => {
        const codice = String(
          cliente.codice_cliente || "",
        ).toLowerCase();

        const ragione = String(
          cliente.ragione_sociale || "",
        ).toLowerCase();

        return (
          codice.includes(valore) ||
          ragione.includes(valore)
        );
      })
      .slice(0, 30);

    setClienti(risultati);
    setShowClienti(risultati.length > 0);
  }, [
    clienteSearch,
    clientiDisponibili,
    embedded,
  ]);

  // ========================================================
  // FUNZIONE PRINCIPALE:
  // FILTRA BACKORDER DEL CLIENTE DIRETTAMENTE DAL JSON
  // ========================================================

  const filtraBackorderCliente = (
    codiceCliente,
    dataInizio = dataDa,
    dataFine = dataA,
  ) => {
    const codice = String(
      codiceCliente || "",
    ).trim();

    if (!codice) {
      return [];
    }

    const righeCliente = allBackorders.filter(
      (item) => {
        return getCodiceCliente(item) === codice;
      },
    );

    /*
     * La riga "testa" contiene KPI, fido, operatore,
     * zona ecc. e deve essere sempre mantenuta.
     *
     * Le altre righe vengono filtrate per Data Doc.
     */
    return righeCliente.filter((item) => {
      if (item?.Prov === "testa") {
        return true;
      }

      const dataDocumento = normalizzaData(
        item?.["Data Doc"],
      );

      /*
       * Se non c'è Data Doc mantengo comunque la riga,
       * così non perdiamo eventuali record particolari.
       */
      if (!dataDocumento) {
        return true;
      }

      if (
        dataInizio &&
        dataDocumento < dataInizio
      ) {
        return false;
      }

      if (
        dataFine &&
        dataDocumento > dataFine
      ) {
        return false;
      }

      return true;
    });
  };

  // ========================================================
  // ESEGUE RICERCA LOCALE
  // ========================================================

  const caricaClienteDaJson = (
    codiceCliente,
    ragione = "",
    dataInizio = dataDa,
    dataFine = dataA,
  ) => {
    const codice = String(
      codiceCliente || "",
    ).trim();

    if (!codice) {
      setBackorders([]);
      setSearched(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const risultati =
        filtraBackorderCliente(
          codice,
          dataInizio,
          dataFine,
        );

      console.log(
        `BACKORDER CLIENTE ${codice}:`,
        risultati,
      );

      setIdCliente(codice);

      if (ragione) {
        setRagioneSociale(ragione);

        setClienteSearch(
          `${ragione} (${codice})`,
        );
      }

      setBackorders(risultati);
      setSearched(true);
    } catch (err) {
      console.error(
        "Errore filtro Back Order:",
        err,
      );

      setError(
        "Errore durante il filtro del Back Order.",
      );

      setBackorders([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  // ========================================================
  // CLIENTE DA URL / ANAGRAFICA EMBEDDED
  // ========================================================

  useEffect(() => {
    /*
     * Prima priorità:
     * initialClientId ricevuto da AddContatti.
     *
     * Seconda priorità:
     * id_cliente presente nella URL.
     */

    const idDaUrl =
      String(initialClientId || "").trim() ||
      String(
        searchParams.get("id_cliente") || "",
      ).trim();

    const ragioneDaUrl =
      String(
        initialRagioneSociale ||
          searchParams.get(
            "ragione_sociale",
          ) ||
          "",
      ).trim();

    if (!idDaUrl) {
      return;
    }

    setIdCliente(idDaUrl);
    setRagioneSociale(ragioneDaUrl);

    setClienteSearch(
      ragioneDaUrl
        ? `${ragioneDaUrl} (${idDaUrl})`
        : idDaUrl,
    );

    setShowClienti(false);
  }, [
    initialClientId,
    initialRagioneSociale,
    searchParams,
  ]);

  // ========================================================
  // CARICAMENTO AUTOMATICO DOPO IL JSON
  // ========================================================

  useEffect(() => {
    if (loadingJson) {
      return;
    }

    if (!allBackorders.length) {
      return;
    }

    const codiceAutomatico =
      String(initialClientId || "").trim() ||
      String(
        searchParams.get("id_cliente") || "",
      ).trim();

    if (!codiceAutomatico) {
      return;
    }

    const ragioneAutomatica =
      String(
        initialRagioneSociale ||
          searchParams.get(
            "ragione_sociale",
          ) ||
          "",
      ).trim();

    caricaClienteDaJson(
      codiceAutomatico,
      ragioneAutomatica,
      dataDa,
      dataA,
    );
  }, [
    loadingJson,
    allBackorders,
    initialClientId,
    initialRagioneSociale,
  ]);

  // ========================================================
  // CHIUDI DROPDOWN CLICCANDO FUORI
  // ========================================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        clienteRef.current &&
        !clienteRef.current.contains(
          event.target,
        )
      ) {
        setShowClienti(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside,
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside,
      );
    };
  }, []);

  // ========================================================
  // SELEZIONE CLIENTE
  // ========================================================

  const selezionaCliente = (cliente) => {
    const codice = String(
      cliente.codice_cliente || "",
    ).trim();

    const ragione = String(
      cliente.ragione_sociale || "",
    ).trim();

    setIdCliente(codice);
    setRagioneSociale(ragione);

    setClienteSearch(
      ragione
        ? `${ragione} (${codice})`
        : codice,
    );

    setClienti([]);
    setShowClienti(false);

    /*
     * Non serve più premere CERCA.
     * Appena selezioniamo il cliente,
     * mostriamo subito il Back Order.
     */
    caricaClienteDaJson(
      codice,
      ragione,
      dataDa,
      dataA,
    );
  };

  // ========================================================
  // RESET CLIENTE
  // ========================================================

  const resetCliente = () => {
    setIdCliente("");
    setRagioneSociale("");
    setClienteSearch("");

    setClienti([]);
    setShowClienti(false);

    setBackorders([]);

    setError("");
    setSearched(false);
  };

  // ========================================================
  // PULSANTE CERCA
  // SOLO FILTRO LOCALE
  // ========================================================

  const cercaBackorder = () => {
    setError("");

    if (!idCliente.trim()) {
      setError("Seleziona un cliente.");
      return;
    }

    if (!dataDa) {
      setError("Inserisci la Data Da.");
      return;
    }

    if (!dataA) {
      setError("Inserisci la Data A.");
      return;
    }

    if (dataDa > dataA) {
      setError(
        "La Data Da non può essere maggiore della Data A.",
      );

      return;
    }

    caricaClienteDaJson(
      idCliente,
      ragioneSociale,
      dataDa,
      dataA,
    );
  };

  // ========================================================
  // RESET COMPLETO
  // ========================================================

  const resetFiltri = () => {
    setIdCliente("");
    setRagioneSociale("");
    setClienteSearch("");

    setClienti([]);
    setShowClienti(false);

    setDataDa("2026-01-01");
    setDataA(oggi);

    setBackorders([]);

    setError("");
    setSearched(false);
  };

  // ========================================================
  // FORMATTA NUMERI
  // ========================================================

  const formatNumber = (value) => {
    const number = Number(value);

    if (isNaN(number)) {
      return "-";
    }

    return new Intl.NumberFormat("it-IT", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(number);
  };

  // ========================================================
  // FORMATTA EURO
  // ========================================================

  const formatEuro = (value) => {
    const number = Number(value);

    if (isNaN(number)) {
      return "-";
    }

    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);
  };

  // ========================================================
  // FORMATTA DATA
  // ========================================================

  const formatDate = (value) => {
    if (!value) {
      return "-";
    }

    const dateString = String(value);

    const match = dateString.match(
      /^(\d{4})-(\d{2})-(\d{2})/,
    );

    if (match) {
      const [, year, month, day] = match;

      return `${day}/${month}/${year}`;
    }

    return dateString;
  };

  // ========================================================
  // TESTATA CLIENTE
  // ========================================================

  const clienteInfo = useMemo(() => {
    if (!backorders.length) {
      return null;
    }

    const testata = backorders.find(
      (item) => item.Prov === "testa",
    );

    return testata || backorders[0];
  }, [backorders]);

  // ========================================================
  // DOCUMENTI
  // ========================================================

  const righeDocumenti = useMemo(() => {
    return backorders.filter(
      (item) => item.Prov !== "testa",
    );
  }, [backorders]);

  // ========================================================
  // TOTALI
  // ========================================================

  const totaleBO = Number(
    clienteInfo?.["TOT BO"] || 0,
  );

  const totaleEvadibile = Number(
    clienteInfo?.["BO Evadibile"] || 0,
  );

  const {
    totaleConOdp,
    totaleSenzaOdp,
  } = useMemo(() => {
    return righeDocumenti.reduce(
      (totali, item) => {
        const importo =
          Number(item?.Importo) || 0;

        const valoreOdp = String(
          item?.ODP ?? "",
        )
          .trim()
          .toLowerCase();

        const haOdp =
          valoreOdp !== "" &&
          valoreOdp !== "-" &&
          valoreOdp !== "0" &&
          valoreOdp !== "no" &&
          valoreOdp !== "null" &&
          valoreOdp !== "undefined";

        if (haOdp) {
          totali.totaleConOdp +=
            importo;
        } else {
          totali.totaleSenzaOdp +=
            importo;
        }

        return totali;
      },
      {
        totaleConOdp: 0,
        totaleSenzaOdp: 0,
      },
    );
  }, [righeDocumenti]);

  const totaleImportoDocumenti =
    useMemo(() => {
      return righeDocumenti.reduce(
        (totale, item) =>
          totale +
          (Number(item?.Importo) || 0),
        0,
      );
    }, [righeDocumenti]);

  const fidoConcesso = Number(
    clienteInfo?.["Fido Concesso"] || 0,
  );

  const fidoDisponibile = Number(
    clienteInfo?.["Fido Disponibile"] || 0,
  );

  // ========================================================
  // RENDER
  // ========================================================

  return (
    <div
      className={`uw-liquid-page ${
        embedded
          ? "uw-backorder-embedded"
          : ""
      }`}
      style={{
        backgroundColor: embedded
          ? "transparent"
          : BG_PAGE,

        width: "100%",

        height: embedded
          ? "auto"
          : "100vh",

        minHeight: embedded
          ? "0"
          : undefined,

        overflow: embedded
          ? "visible"
          : "hidden",

        color: TEXT_DARK,

        fontFamily:
          "'Inter', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* ==================================================
          NAVBAR
      =================================================== */}

      {!embedded && (
        <NavbarUtility title="Back Order" />
      )}

      <Container
        fluid
        className={
          embedded
            ? "uw-backorder-content p-0"
            : "uw-page-scroll py-4 px-3 px-md-5"
        }
      >
        {/* =================================================
            LOADING JSON
        ================================================== */}

        {loadingJson && (
          <div className="uw-json-loading">
            <Spinner
              animation="border"
              size="sm"
            />

            <span>
              Caricamento Back Order...
            </span>
          </div>
        )}

        {/* =================================================
            FILTRI
            NASCOSTI IN MODALITÀ EMBEDDED
        ================================================== */}

        {!embedded && !loadingJson && (
          <Card
            className="uw-card uw-search-card border-0 mb-4 overflow-visible"
            style={{
              backgroundColor: PANEL_BG,
            }}
          >
            <div className="uw-card-accent" />

            <Card.Body className="p-4">
              <div className="d-flex align-items-center mb-4">
                <span
                  style={{
                    width: "16px",
                    height: "2px",
                    backgroundColor: GOLD,
                    display: "inline-block",
                    marginRight: "8px",
                  }}
                />

                <h6
                  className="mb-0 fw-bold text-uppercase"
                  style={{
                    color: TEXT_DARK,
                    fontSize: "0.8rem",
                    letterSpacing: "1px",
                  }}
                >
                  Ricerca Back Order
                </h6>
              </div>

              <div
                className="p-4"
                style={{
                  backgroundColor:
                    FILTER_BOX_BG,

                  border: `1px solid ${FILTER_BOX_BORDER}`,

                  borderRadius: "16px",
                }}
              >
                <div className="d-flex align-items-center gap-2 mb-3">
                  <Filter
                    size={15}
                    color={GOLD_DARK}
                  />

                  <span
                    className="text-uppercase fw-bold"
                    style={{
                      fontSize:
                        "0.75rem",

                      color: GOLD_DARK,

                      letterSpacing:
                        "1px",
                    }}
                  >
                    Parametri di Ricerca
                  </span>
                </div>

                <div className="row g-3 align-items-end">
                  {/* =======================================
                      CLIENTE
                  ======================================== */}

                  <div
                    className="col-12 col-lg-5"
                    ref={clienteRef}
                    style={{
                      position:
                        "relative",

                      zIndex: 100,
                    }}
                  >
                    <Form.Label
                      className="fw-semibold mb-1"
                      style={{
                        fontSize:
                          "0.8rem",

                        color: TEXT_DARK,
                      }}
                    >
                      CLIENTE{" "}

                      <span
                        style={{
                          color:
                            GOLD_DARK,
                        }}
                      >
                        *
                      </span>
                    </Form.Label>

                    <div
                      style={{
                        position:
                          "relative",
                      }}
                    >
                      <Form.Control
                        type="text"
                        value={
                          clienteSearch
                        }
                        onChange={(
                          e,
                        ) => {
                          const value =
                            e.target
                              .value;

                          setClienteSearch(
                            value,
                          );

                          /*
                           * Se l'utente modifica
                           * manualmente il testo
                           * annulliamo il cliente
                           * selezionato.
                           */
                          setIdCliente(
                            "",
                          );

                          setRagioneSociale(
                            "",
                          );

                          setBackorders(
                            [],
                          );

                          setSearched(
                            false,
                          );
                        }}
                        onFocus={() => {
                          if (
                            clienteSearch
                              .trim()
                              .length >=
                              2 &&
                            clienti.length >
                              0
                          ) {
                            setShowClienti(
                              true,
                            );
                          }
                        }}
                        placeholder="Cerca ragione sociale o codice cliente..."
                        className="uw-input"
                        style={{
                          paddingRight:
                            "38px",
                        }}
                      />

                      {clienteSearch && (
                        <button
                          type="button"
                          onClick={
                            resetCliente
                          }
                          className="cliente-clear-btn"
                        >
                          <X
                            size={15}
                          />
                        </button>
                      )}
                    </div>

                    {/* =====================================
                        DROPDOWN CLIENTI
                    ====================================== */}

                    {showClienti &&
                      clienteSearch
                        .trim()
                        .length >=
                        2 && (
                        <div className="clienti-dropdown">
                          {clienti.length >
                          0 ? (
                            clienti.map(
                              (
                                cliente,
                                index,
                              ) => (
                                <button
                                  key={
                                    cliente.codice_cliente ||
                                    index
                                  }
                                  type="button"
                                  className="cliente-result"
                                  onClick={() =>
                                    selezionaCliente(
                                      cliente,
                                    )
                                  }
                                >
                                  <div className="cliente-result-main">
                                    {cliente.ragione_sociale ||
                                      "-"}
                                  </div>

                                  <div className="cliente-result-info">
                                    <span>
                                      #
                                      {
                                        cliente.codice_cliente
                                      }
                                    </span>

                                    {cliente.zona && (
                                      <span>
                                        {
                                          cliente.zona
                                        }
                                      </span>
                                    )}

                                    {cliente.nazione && (
                                      <span>
                                        {
                                          cliente.nazione
                                        }
                                      </span>
                                    )}
                                  </div>
                                </button>
                              ),
                            )
                          ) : (
                            <div className="cliente-no-results">
                              Nessun
                              cliente
                              trovato
                            </div>
                          )}
                        </div>
                      )}
                  </div>

                  {/* =======================================
                      DATA DA
                  ======================================== */}

                  <div className="col-12 col-md-4 col-lg-2">
                    <Form.Label
                      className="fw-semibold mb-1"
                      style={{
                        fontSize:
                          "0.8rem",

                        color: TEXT_DARK,
                      }}
                    >
                      DATA DA
                    </Form.Label>

                    <Form.Control
                      type="date"
                      value={dataDa}
                      onChange={(
                        e,
                      ) =>
                        setDataDa(
                          e.target
                            .value,
                        )
                      }
                      className="uw-input"
                    />
                  </div>

                  {/* =======================================
                      DATA A
                  ======================================== */}

                  <div className="col-12 col-md-4 col-lg-2">
                    <Form.Label
                      className="fw-semibold mb-1"
                      style={{
                        fontSize:
                          "0.8rem",

                        color: TEXT_DARK,
                      }}
                    >
                      DATA A
                    </Form.Label>

                    <Form.Control
                      type="date"
                      value={dataA}
                      onChange={(
                        e,
                      ) =>
                        setDataA(
                          e.target
                            .value,
                        )
                      }
                      className="uw-input"
                    />
                  </div>

                  {/* =======================================
                      CERCA
                  ======================================== */}

                  <div className="col-12 col-md-4 col-lg-2">
                    <button
                      onClick={
                        cercaBackorder
                      }
                      disabled={
                        loading ||
                        !idCliente
                      }
                      className="uw-action-btn uw-action-solid w-100"
                      style={{
                        height:
                          "40px",
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                          />

                          <span>
                            Caricamento...
                          </span>
                        </>
                      ) : (
                        <>
                          <Search
                            size={
                              16
                            }
                          />

                          <span>
                            CERCA
                          </span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* =======================================
                      RESET
                  ======================================== */}

                  <div className="col-12 col-lg-1">
                    <button
                      onClick={
                        resetFiltri
                      }
                      disabled={
                        loading
                      }
                      className="uw-action-btn w-100"
                      style={{
                        height:
                          "40px",
                      }}
                      title="Reset filtri"
                    >
                      <RefreshCw
                        size={16}
                      />
                    </button>
                  </div>
                </div>

                {/* =========================================
                    CLIENTE SELEZIONATO
                ========================================== */}

                {idCliente && (
                  <div className="selected-client mt-3">
                    <div>
                      <small>
                        CLIENTE
                        SELEZIONATO
                      </small>

                      <strong>
                        {ragioneSociale ||
                          "-"}
                      </strong>
                    </div>

                    <span>
                      #{idCliente}
                    </span>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* =================================================
            ERRORE
        ================================================== */}

        {error && (
          <Alert
            variant="danger"
            className="uw-glass-alert d-flex align-items-center gap-3 border-0 mb-4"
          >
            <AlertCircle
              size={20}
              className="flex-shrink-0"
            />

            <span
              className="fw-medium"
              style={{
                fontSize:
                  "0.9rem",
              }}
            >
              {error}
            </span>
          </Alert>
        )}

        {/* =================================================
            LOADING CLIENTE
        ================================================== */}

        {embedded &&
          (loadingJson || loading) && (
            <div className="uw-backorder-loading">
              <Spinner
                animation="border"
                size="sm"
              />

              <span>
                Caricamento Back
                Order...
              </span>
            </div>
          )}

        {/* =================================================
            RISULTATI
        ================================================== */}

        {!loadingJson &&
          searched &&
          clienteInfo && (
            <>
              {/* ===========================================
                  TESTATA CLIENTE
              ============================================ */}

              <Card
                className="uw-card border-0 mb-4"
                style={{
                  backgroundColor:
                    "transparent",

                  overflow:
                    "hidden",
                }}
              >
                <div className="uw-card-accent" />

                <Card.Body className="p-4">
                  <div className="row g-4">
                    <div className="col-12 col-lg-4">
                      <div className="client-header-label">
                        CLIENTE
                      </div>

                      <div className="client-header-name">
                        {clienteInfo[
                          "Ragione Sociale"
                        ] ||
                          ragioneSociale}
                      </div>

                      <div className="client-header-code">
                        #
                        {clienteInfo[
                          "ID Cliente"
                        ] ||
                          clienteInfo[
                            "ID cliente"
                          ] ||
                          idCliente}
                      </div>
                    </div>

                    <div className="col-6 col-lg-2">
                      <div className="client-header-label">
                        OPERATORE
                      </div>

                      <div className="client-header-value">
                        {clienteInfo.Operatore ||
                          "-"}
                      </div>
                    </div>

                    <div className="col-6 col-lg-2">
                      <div className="client-header-label">
                        ZONA
                      </div>

                      <div className="client-header-value">
                        {clienteInfo.Zona ||
                          "-"}
                      </div>
                    </div>

                    <div className="col-6 col-lg-2">
                      <div className="client-header-label">
                        NAZIONE
                      </div>

                      <div className="client-header-value">
                        {clienteInfo.Nazione ||
                          "-"}
                      </div>
                    </div>

                    <div className="col-6 col-lg-2">
                      <div className="client-header-label">
                        AGENTE
                      </div>

                      <div className="client-header-value">
                        {clienteInfo.Agente ||
                          "-"}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* ===========================================
                  KPI
              ============================================ */}

              <div className="row g-3 mb-4">
                <div className="col-12 col-md-6 col-xl">
                  <div className="bo-kpi">
                    <span>
                      TOTALE BACKORDER
                    </span>

                    <strong>
                      {formatEuro(
                        totaleBO,
                      )}
                    </strong>
                  </div>
                </div>

                <div className="col-12 col-md-6 col-xl">
                  <div className="bo-kpi">
                    <span>
                      BO EVADIBILE
                    </span>

                    <strong>
                      {formatEuro(
                        totaleEvadibile,
                      )}
                    </strong>
                  </div>
                </div>

                <div className="col-12 col-md-6 col-xl">
                  <div className="bo-kpi">
                    <span>
                      SOMMA CON ODP
                    </span>

                    <strong>
                      {formatEuro(
                        totaleConOdp,
                      )}
                    </strong>
                  </div>
                </div>

                <div className="col-12 col-md-6 col-xl">
                  <div className="bo-kpi">
                    <span>
                      SOMMA SENZA ODP
                    </span>

                    <strong>
                      {formatEuro(
                        totaleSenzaOdp,
                      )}
                    </strong>
                  </div>
                </div>

                <div className="col-12 col-md-6 col-xl">
                  <div className="bo-kpi">
                    <span>
                      FIDO DISPONIBILE
                    </span>

                    <strong>
                      {formatEuro(
                        fidoDisponibile,
                      )}
                    </strong>

                    <small>
                      Concesso:{" "}
                      {formatEuro(
                        fidoConcesso,
                      )}
                    </small>
                  </div>
                </div>
              </div>

              {/* ===========================================
                  SITUAZIONE BACKORDER
              ============================================ */}

              <Card className="uw-card border-0 mb-4">
                <Card.Body className="p-0">
                  <div className="bo-summary-header">
                    <div>
                      <span>
                        SITUAZIONE
                        BACKORDER
                      </span>

                      <small>
                        Cliente #
                        {idCliente}
                      </small>
                    </div>
                  </div>

                  <div className="row g-0">
                    <div className="col-6 col-md-3">
                      <div className="bo-summary-item">
                        <small>
                          TOT ORDINI
                        </small>

                        <strong>
                          {formatEuro(
                            clienteInfo[
                              "TOT Ordini"
                            ],
                          )}
                        </strong>
                      </div>
                    </div>

                    <div className="col-6 col-md-3">
                      <div className="bo-summary-item">
                        <small>
                          BO &lt; 1
                          MESE
                        </small>

                        <strong>
                          {formatEuro(
                            clienteInfo[
                              "TOT BO <1mese"
                            ],
                          )}
                        </strong>
                      </div>
                    </div>

                    <div className="col-6 col-md-3">
                      <div className="bo-summary-item">
                        <small>
                          BO 1-3 MESI
                        </small>

                        <strong>
                          {formatEuro(
                            clienteInfo[
                              "TOT BO 1<3mesi"
                            ],
                          )}
                        </strong>
                      </div>
                    </div>

                    <div className="col-6 col-md-3">
                      <div className="bo-summary-item">
                        <small>
                          BO &gt; 3
                          MESI
                        </small>

                        <strong>
                          {formatEuro(
                            clienteInfo[
                              "TOT BO >3mesi"
                            ],
                          )}
                        </strong>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* ===========================================
                  TABELLA DOCUMENTI
              ============================================ */}

              <div className="table-responsive scroll-hidden">
                <Table
                  hover
                  align="middle"
                  className="mb-0 uw-table"
                  style={{
                    minWidth:
                      "1100px",
                  }}
                >
                  <thead>
                    <tr>
                      <th>
                        ANNO
                      </th>

                      <th>
                        TIPO DOC
                      </th>

                      <th>
                        NUM DOC
                      </th>

                      <th>
                        DATA DOC
                      </th>

                      <th>
                        DEP
                      </th>

                      <th>
                        IMPORTO
                      </th>

                      <th>
                        ODP
                      </th>

                      <th>
                        TIPOLOGIA
                      </th>

                      <th>
                        ORE ODP
                      </th>

                      <th>
                        MC
                      </th>

                      <th>
                        NOTE
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {righeDocumenti.length >
                    0 ? (
                      righeDocumenti.map(
                        (
                          item,
                          index,
                        ) => (
                          <tr
                            key={`${item["Num Doc"] || "row"}-${index}`}
                          >
                            <td>
                              {item[
                                "Anno Doc"
                              ] ||
                                "-"}
                            </td>

                            <td className="fw-bold">
                              {item[
                                "Tipo Doc"
                              ] ||
                                "-"}
                            </td>

                            <td className="fw-bold">
                              {item[
                                "Num Doc"
                              ] ||
                                "-"}
                            </td>

                            <td className="text-nowrap">
                              {formatDate(
                                item[
                                  "Data Doc"
                                ],
                              )}
                            </td>

                            <td>
                              {item.Dep ||
                                "-"}
                            </td>

                            <td className="text-end fw-bold text-nowrap">
                              {formatEuro(
                                item.Importo,
                              )}
                            </td>

                            <td>
                              {item.ODP ||
                                "-"}
                            </td>

                            <td>
                              {item.tipologia ||
                                "-"}
                            </td>

                            <td>
                              {item[
                                "Ore ODP"
                              ] ||
                                "-"}
                            </td>

                            <td>
                              {item.MC ||
                                "-"}
                            </td>

                            <td
                              className="text-truncate"
                              style={{
                                maxWidth:
                                  "250px",
                              }}
                              title={
                                item.Note ||
                                ""
                              }
                            >
                              {item.Note ||
                                "-"}
                            </td>
                          </tr>
                        ),
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={
                            11
                          }
                          className="text-center py-5"
                        >
                          <PackageSearch
                            size={
                              42
                            }
                            className="mb-2"
                            style={{
                              opacity:
                                0.3,

                              color:
                                GOLD_DARK,
                            }}
                          />

                          <div className="fw-bold text-uppercase">
                            Nessun
                            documento
                          </div>

                          <small>
                            Non ci
                            sono
                            documenti
                            nel
                            periodo
                            selezionato.
                          </small>
                        </td>
                      </tr>
                    )}
                  </tbody>

                  {righeDocumenti.length >
                    0 && (
                    <tfoot>
                      <tr className="uw-table-total-row">
                        <td
                          colSpan={
                            5
                          }
                          className="text-end"
                        >
                          TOTALE
                          IMPORTO
                        </td>

                        <td className="text-end fw-bold text-nowrap">
                          {formatEuro(
                            totaleImportoDocumenti,
                          )}
                        </td>

                        <td
                          colSpan={
                            5
                          }
                        />
                      </tr>
                    </tfoot>
                  )}
                </Table>
              </div>
            </>
          )}

        {/* =================================================
            NESSUN RISULTATO
        ================================================== */}

        {!loadingJson &&
          searched &&
          !clienteInfo && (
            <Card className="uw-card border-0">
              <Card.Body className="text-center py-5">
                <PackageSearch
                  size={48}
                  style={{
                    opacity: 0.3,
                    color:
                      GOLD_DARK,
                  }}
                />

                <h6 className="mt-3 fw-bold">
                  Nessun Back Order
                  trovato
                </h6>

                <small>
                  Il cliente non
                  presenta dati nel
                  periodo selezionato.
                </small>
              </Card.Body>
            </Card>
          )}
      </Container>

      {/* ===================================================
          CSS
      ==================================================== */}

      <style>{`

        .uw-card {
          box-shadow: 0 4px 16px rgba(0,0,0,0.06);
        }

        /* ==========================================
           LOADING
        ========================================== */

        .uw-json-loading,
        .uw-backorder-loading {
          min-height: 180px;

          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;

          color: #a9aaad;

          font-size: 0.82rem;
          font-weight: 600;
        }

        /* ==========================================
           INPUT
        ========================================== */

        .uw-input {
          border: 1px solid rgba(0,0,0,0.15) !important;

          background-color: #ffffff !important;

          font-size: 0.85rem !important;

          color: ${TEXT_DARK} !important;

          padding: 8px 12px !important;
        }

        .uw-input:focus {
          border-color: ${GOLD} !important;

          box-shadow:
            0 0 0 2px rgba(235,181,27,0.2) !important;

          outline: none !important;
        }

        /* ==========================================
           BUTTON
        ========================================== */

        .uw-action-btn {
          display: inline-flex;

          align-items: center;
          justify-content: center;

          gap: 6px;

          border: 1px solid ${GOLD};

          background-color: transparent;

          color: ${GOLD_DARK};

          font-size: 0.8rem;
          font-weight: 700;

          letter-spacing: 0.5px;

          text-transform: uppercase;

          padding: 6px 12px;

          transition: all 0.15s ease-in-out;

          cursor: pointer;
        }

        .uw-action-btn:hover:not(:disabled) {
          background-color:
            rgba(235,181,27,0.12);
        }

        .uw-action-btn:disabled {
          opacity: 0.5;

          cursor: not-allowed;
        }

        .uw-action-solid {
          background-color: ${GOLD};

          color: ${TEXT_DARK};

          border-color: ${GOLD};
        }

        /* ==========================================
           DROPDOWN CLIENTI
        ========================================== */

        .clienti-dropdown {
          position: absolute;

          top: calc(100% + 4px);

          left: 12px;
          right: 12px;

          background: #ffffff;

          border:
            1px solid rgba(0,0,0,0.15);

          box-shadow:
            0 8px 24px rgba(0,0,0,0.15);

          z-index: 99999;

          max-height: 320px;

          overflow-y: auto;
        }

        .cliente-result {
          display: block;

          width: 100%;

          border: 0;

          border-bottom:
            1px solid rgba(0,0,0,0.08);

          background: #ffffff;

          padding: 11px 14px;

          text-align: left;

          cursor: pointer;

          transition:
            background 0.12s ease;
        }

        .cliente-result:hover {
          background:
            rgba(235,181,27,0.10);
        }

        .cliente-result-main {
          color: ${TEXT_DARK};

          font-size: 0.85rem;

          font-weight: 700;
        }

        .cliente-result-info {
          display: flex;

          flex-wrap: wrap;

          gap: 14px;

          margin-top: 3px;

          color: ${TEXT_MUTED};

          font-size: 0.72rem;
        }

        .cliente-no-results {
          padding: 15px;

          text-align: center;

          color: ${TEXT_MUTED};

          font-size: 0.8rem;
        }

        .cliente-clear-btn {
          position: absolute;

          right: 10px;

          top: 50%;

          transform: translateY(-50%);

          border: 0;

          background: transparent;

          color: ${TEXT_MUTED};

          cursor: pointer;

          padding: 3px;

          z-index: 3;
        }

        .cliente-clear-btn:hover {
          color: ${TEXT_DARK};
        }

        /* ==========================================
           CLIENTE SELEZIONATO
        ========================================== */

        .selected-client {
          display: flex;

          align-items: center;

          justify-content: space-between;

          padding: 9px 12px;
        }

        .selected-client small {
          display: block;

          color: ${TEXT_MUTED};

          font-size: 0.65rem;

          letter-spacing: 0.8px;

          font-weight: 700;
        }

        .selected-client strong {
          display: block;

          color: ${TEXT_DARK};

          font-size: 0.85rem;

          margin-top: 2px;
        }

        .selected-client > span {
          font-weight: 700;

          color: ${GOLD_DARK};

          font-size: 0.8rem;
        }

        /* ==========================================
           CLIENT HEADER
        ========================================== */

        .client-header-label {
          color: ${TEXT_MUTED};

          font-size: 0.68rem;

          font-weight: 700;

          letter-spacing: 0.8px;

          margin-bottom: 4px;
        }

        .client-header-name {
          color: ${TEXT_DARK};

          font-size: 1.25rem;

          font-weight: 700;
        }

        .client-header-code {
          color: ${GOLD_DARK};

          font-size: 0.8rem;

          font-weight: 600;

          margin-top: 3px;
        }

        .client-header-value {
          color: ${TEXT_DARK};

          font-size: 0.85rem;

          font-weight: 600;
        }

        /* ==========================================
           KPI
        ========================================== */

        .bo-kpi {
          position: relative;

          overflow: hidden;

          min-height: 116px;

          padding: 18px;

          border:
            1px solid rgba(255,255,255,.10);

          border-top:
            1px solid rgba(235,181,27,.34);

          border-radius: 18px;

          background:
            radial-gradient(
              circle at 0 0,
              rgba(235,181,27,.10),
              transparent 42%
            ),
            linear-gradient(
              145deg,
              rgba(255,255,255,.085),
              rgba(255,255,255,.025)
            );

          box-shadow:
            0 12px 28px rgba(0,0,0,.24),
            inset 0 1px 0 rgba(255,255,255,.065);

          backdrop-filter:
            blur(18px) saturate(130%);

          -webkit-backdrop-filter:
            blur(18px) saturate(130%);
        }

        .bo-kpi::before {
          content: "";

          position: absolute;

          left: 16px;
          right: 16px;
          top: 0;

          height: 1px;

          background:
            linear-gradient(
              90deg,
              transparent,
              rgba(235,181,27,.78),
              transparent
            );
        }

        .bo-kpi span {
          display: block;

          color: #b9bac0;

          font-size: 0.68rem;

          font-weight: 700;

          letter-spacing: .07em;

          margin-bottom: 8px;
        }

        .bo-kpi strong {
          display: block;

          color: #f7f7f4;

          font-size: 1.22rem;

          font-weight: 700;
        }

        .bo-kpi small {
          display: block;

          color: #8f9197;

          margin-top: 4px;

          font-size: 0.7rem;
        }

        /* ==========================================
           SUMMARY
        ========================================== */

        .bo-summary-header {
          padding: 15px 18px;

          display: flex;

          justify-content:
            space-between;
        }

        .bo-summary-header span {
          display: block;

          font-weight: 700;

          font-size: 0.75rem;

          letter-spacing: 0.8px;
        }

        .bo-summary-header small {
          color: ${TEXT_MUTED};

          font-size: 0.7rem;
        }

        .bo-summary-item {
          border-right:
            1px solid rgba(255,255,255,.08);

          padding: 16px;
        }

        .bo-summary-item small {
          display: block;

          color: ${TEXT_MUTED};

          font-size: 0.65rem;

          font-weight: 700;

          margin-bottom: 6px;
        }

        .bo-summary-item strong {
          color: ${TEXT_DARK};

          font-size: 1rem;
        }

        /* ==========================================
           LIQUID PAGE
        ========================================== */

        .uw-liquid-page {
          --uw-gold: #ebb51b;
          --uw-gold-dark: #a67a10;

          --uw-bg: #181b21;

          --uw-panel:
            rgba(43,47,56,.78);

          --uw-white: #f7f7f4;

          --uw-text: #f1f1ee;

          --uw-muted: #a9aaad;

          --uw-muted-2: #81838a;

          --uw-glass-border:
            rgba(255,255,255,.11);

          --uw-gold-border:
            rgba(235,181,27,.36);

          --uw-shadow:
            0 22px 54px rgba(0,0,0,.40);

          color: var(--uw-text);

          background:
            radial-gradient(
              circle at 12% 8%,
              rgba(235,181,27,.10),
              transparent 28%
            ),
            radial-gradient(
              circle at 88% 14%,
              rgba(255,255,255,.045),
              transparent 25%
            ),
            radial-gradient(
              circle at 62% 85%,
              rgba(166,122,16,.055),
              transparent 34%
            ),
            linear-gradient(
              145deg,
              #191c22 0%,
              #242831 52%,
              #15181e 100%
            ) !important;
        }

        .uw-liquid-page,
        .uw-liquid-page .card,
        .uw-liquid-page .table,
        .uw-liquid-page .form-control,
        .uw-liquid-page .form-select {
          color-scheme: dark;
        }

        /* ==========================================
           NAVBAR
        ========================================== */

        .uw-liquid-page > nav,
        .uw-liquid-page > .navbar,
        .uw-liquid-page .navbar {
          background:
            rgba(22,25,31,.80) !important;

          backdrop-filter:
            blur(24px) saturate(145%);

          -webkit-backdrop-filter:
            blur(24px) saturate(145%);

          border-bottom:
            1px solid
            rgba(235,181,27,.23) !important;

          box-shadow:
            0 12px 34px
              rgba(0,0,0,.32),

            inset 0 -1px 0
              rgba(255,255,255,.04);

          color:
            var(--uw-white) !important;
        }

        .uw-liquid-page .navbar * {
          color: inherit;
        }

        /* ==========================================
           SCROLL PAGINA NORMALE
        ========================================== */

        .uw-liquid-page .uw-page-scroll {
          position: relative;

          height:
            calc(100vh - 60px);

          max-height: none;

          min-height: 0;

          background: transparent;

          overflow-y: auto;

          overflow-x: hidden;

          box-sizing:
            border-box;

          padding-bottom:
            64px !important;

          scroll-padding-bottom:
            64px;
        }

        /* ==========================================
           CARD
        ========================================== */

        .uw-liquid-page .uw-card {
          position: relative;

          background:
            radial-gradient(
              circle at 12% 0%,
              rgba(255,255,255,.070),
              transparent 31%
            ),
            linear-gradient(
              145deg,
              rgba(62,62,63,.88),
              rgba(74,81,94,.78)
            ) !important;

          border:
            1px solid
            var(--uw-glass-border) !important;

          border-radius:
            24px !important;

          box-shadow:
            var(--uw-shadow),

            inset 0 1px 0
              rgba(255,255,255,.08),

            inset 0 -1px 0
              rgba(0,0,0,.35) !important;

          backdrop-filter:
            blur(24px) saturate(145%);

          -webkit-backdrop-filter:
            blur(24px) saturate(145%);

          color:
            var(--uw-text);
        }

        .uw-liquid-page .uw-card::after {
          content: "";

          position: absolute;

          inset: 0;

          pointer-events: none;

          border-radius: inherit;

          background:
            linear-gradient(
              115deg,
              rgba(255,255,255,.045),
              transparent 24%,
              transparent 74%,
              rgba(235,181,27,.025)
            );
        }

        .uw-liquid-page .uw-card > * {
          position: relative;

          z-index: 1;
        }

        .uw-liquid-page .uw-card-accent {
          height: 2px;

          width: 100%;

          background:
            linear-gradient(
              90deg,
              transparent 0%,
              rgba(235,181,27,.32) 12%,
              #ebb51b 48%,
              rgba(166,122,16,.76) 78%,
              transparent 100%
            );

          box-shadow:
            0 0 18px
            rgba(235,181,27,.22);
        }

        /* ==========================================
           TYPOGRAPHY
        ========================================== */

        .uw-liquid-page h1,
        .uw-liquid-page h2,
        .uw-liquid-page h3,
        .uw-liquid-page h4,
        .uw-liquid-page h5,
        .uw-liquid-page h6,
        .uw-liquid-page strong,
        .uw-liquid-page .fw-semibold,
        .uw-liquid-page .fw-bold {
          color:
            var(--uw-white) !important;
        }

        .uw-liquid-page .text-muted,
        .uw-liquid-page small,
        .uw-liquid-page .small {
          color:
            var(--uw-muted) !important;
        }

        /* ==========================================
           INPUT DARK GLASS
        ========================================== */

        .uw-liquid-page .uw-input,
        .uw-liquid-page .form-control.uw-input,
        .uw-liquid-page .form-select.uw-input {
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,.090),
              rgba(255,255,255,.038)
            ) !important;

          border:
            1px solid
            rgba(255,255,255,.115) !important;

          color:
            var(--uw-white) !important;

          -webkit-text-fill-color:
            var(--uw-white);

          border-radius:
            12px !important;

          min-height: 40px;

          box-shadow:
            inset 0 1px 0
              rgba(255,255,255,.070),

            0 4px 14px
              rgba(0,0,0,.16);

          backdrop-filter:
            blur(12px);

          -webkit-backdrop-filter:
            blur(12px);

          transition:
            border-color .18s ease,
            box-shadow .18s ease,
            background .18s ease;
        }

        .uw-liquid-page .uw-input::placeholder {
          color:
            #888b91 !important;

          -webkit-text-fill-color:
            #888b91;

          opacity: 1;
        }

        .uw-liquid-page .uw-input:hover:not(:focus) {
          background:
            rgba(255,255,255,.085) !important;

          border-color:
            rgba(235,181,27,.24) !important;
        }

        .uw-liquid-page .uw-input:focus {
          background:
            rgba(255,255,255,.10) !important;

          border-color:
            rgba(235,181,27,.68) !important;

          box-shadow:
            0 0 0 3px
              rgba(235,181,27,.11),

            0 8px 24px
              rgba(0,0,0,.22) !important;
        }

        .uw-liquid-page input[type="date"] {
          color-scheme: dark;
        }

        /* ==========================================
           ACTION BUTTON
        ========================================== */

        .uw-liquid-page .uw-action-btn {
          border:
            1px solid
            rgba(235,181,27,.34);

          border-radius: 12px;

          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,.080),
              rgba(255,255,255,.025)
            );

          color: #e6bd4d;

          box-shadow:
            inset 0 1px 0
              rgba(255,255,255,.055),

            0 7px 18px
              rgba(0,0,0,.18);

          backdrop-filter:
            blur(14px);

          -webkit-backdrop-filter:
            blur(14px);
        }

        .uw-liquid-page
        .uw-action-btn:hover:not(:disabled) {
          color: #fff;

          border-color:
            rgba(235,181,27,.58);

          background:
            linear-gradient(
              145deg,
              rgba(235,181,27,.18),
              rgba(255,255,255,.055)
            );

          transform:
            translateY(-1px);
        }

        .uw-liquid-page .uw-action-solid {
          background:
            linear-gradient(
              145deg,
              #f0c43e,
              #a67a10
            ) !important;

          color:
            #17191e !important;

          border-color:
            rgba(235,181,27,.72) !important;

          box-shadow:
            0 9px 22px
              rgba(0,0,0,.24),

            inset 0 1px 0
              rgba(255,255,255,.30);
        }

        /* ==========================================
           CLIENT SELECTED / SUMMARY
        ========================================== */

        .uw-liquid-page .selected-client,
        .uw-liquid-page .bo-summary-header,
        .uw-liquid-page .bo-summary-item {
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,.075),
              rgba(255,255,255,.025)
            ) !important;

          border-color:
            rgba(255,255,255,.085) !important;

          color:
            var(--uw-text) !important;
        }

        .uw-liquid-page .selected-client {
          border:
            1px solid
            rgba(255,255,255,.09);

          border-left:
            3px solid
            rgba(235,181,27,.62);

          border-radius: 14px;

          box-shadow:
            inset 0 1px 0
              rgba(255,255,255,.05),

            0 8px 22px
              rgba(0,0,0,.18);
        }

        .uw-liquid-page
        .selected-client small,
        .uw-liquid-page
        .client-header-label,
        .uw-liquid-page
        .bo-summary-header small,
        .uw-liquid-page
        .bo-summary-item small {
          color:
            var(--uw-muted) !important;
        }

        .uw-liquid-page
        .selected-client strong,
        .uw-liquid-page
        .client-header-name,
        .uw-liquid-page
        .client-header-value,
        .uw-liquid-page
        .bo-summary-header span,
        .uw-liquid-page
        .bo-summary-item strong {
          color:
            var(--uw-white) !important;
        }

        .uw-liquid-page
        .client-header-code,
        .uw-liquid-page
        .selected-client > span {
          color:
            #e6bd4d !important;
        }

        /* ==========================================
           AUTOCOMPLETE DARK
        ========================================== */

        .uw-liquid-page .clienti-dropdown {
          background:
            linear-gradient(
              145deg,
              rgba(48,52,61,.98),
              rgba(31,34,41,.98)
            );

          border:
            1px solid
            rgba(255,255,255,.11);

          border-radius: 14px;

          box-shadow:
            0 20px 44px
            rgba(0,0,0,.44);

          backdrop-filter:
            blur(20px);

          -webkit-backdrop-filter:
            blur(20px);

          overflow:
            hidden auto;
        }

        .uw-liquid-page .cliente-result {
          background:
            transparent;

          border-bottom:
            1px solid
            rgba(255,255,255,.06);
        }

        .uw-liquid-page .cliente-result:hover {
          background:
            rgba(235,181,27,.10);
        }

        .uw-liquid-page
        .cliente-result-main {
          color:
            var(--uw-white);
        }

        .uw-liquid-page
        .cliente-result-info,
        .uw-liquid-page
        .cliente-no-results,
        .uw-liquid-page
        .cliente-clear-btn {
          color:
            var(--uw-muted);
        }

        .uw-liquid-page
        .cliente-clear-btn:hover {
          color: #fff;
        }

        /* ==========================================
           TABLE
        ========================================== */

        .uw-liquid-page .uw-table {
          --bs-table-bg:
            transparent;

          --bs-table-color:
            var(--uw-text);

          --bs-table-hover-bg:
            rgba(235,181,27,.045);

          border-color:
            rgba(255,255,255,.07);

          background:
            transparent !important;
        }

        .uw-liquid-page
        .uw-table thead th {
          background:
            rgba(255,255,255,.070) !important;

          color:
            #d6b34d !important;

          border-color:
            rgba(255,255,255,.075) !important;

          font-size:
            0.70rem !important;

          text-transform:
            uppercase;

          letter-spacing:
            0.5px;

          font-weight:
            700 !important;

          padding:
            10px 14px;

          white-space:
            nowrap;
        }

        .uw-liquid-page
        .uw-table tbody td {
          background:
            rgba(255,255,255,.035) !important;

          color:
            #ecece8 !important;

          border-color:
            rgba(255,255,255,.055) !important;

          font-size:
            0.82rem;

          padding:
            10px 14px;
        }

        .uw-liquid-page
        .uw-table tbody tr:nth-child(even) td {
          background:
            rgba(255,255,255,.048) !important;
        }

        .uw-liquid-page
        .uw-table tbody tr:hover td {
          background:
            rgba(235,181,27,.075) !important;
        }

        .uw-liquid-page
        .uw-table tfoot td {
          background:
            rgba(235,181,27,.10) !important;

          color:
            #f1cf72 !important;

          border-top:
            1px solid
            rgba(235,181,27,.30) !important;

          border-bottom:
            0 !important;

          padding:
            12px 14px;

          font-size:
            .78rem;

          letter-spacing:
            .04em;
        }

        .uw-liquid-page
        .uw-table-total-row td {
          box-shadow:
            inset 0 1px 0
            rgba(255,255,255,.035);
        }

        .uw-liquid-page
        .table-responsive {
          border:
            1px solid
            rgba(255,255,255,.085);

          border-radius: 18px;

          overflow: auto;

          box-shadow:
            0 14px 34px
              rgba(0,0,0,.23),

            inset 0 1px 0
              rgba(255,255,255,.045);

          backdrop-filter:
            blur(14px);

          -webkit-backdrop-filter:
            blur(14px);
        }

        .scroll-hidden {
          scrollbar-width: none;

          -ms-overflow-style:
            none;
        }

        .scroll-hidden::-webkit-scrollbar {
          display: none;
        }

        /* ==========================================
           ALERT
        ========================================== */

        .uw-liquid-page
        .uw-glass-alert {
          border:
            1px solid
            rgba(211,81,81,.30) !important;

          border-left:
            3px solid
            rgba(211,81,81,.80) !important;

          border-radius:
            16px !important;

          background:
            linear-gradient(
              145deg,
              rgba(150,45,45,.20),
              rgba(255,255,255,.035)
            ) !important;

          color:
            #f2c5c5 !important;

          box-shadow:
            0 12px 28px
              rgba(0,0,0,.20),

            inset 0 1px 0
              rgba(255,255,255,.05);

          backdrop-filter:
            blur(16px);

          -webkit-backdrop-filter:
            blur(16px);
        }

        /* ==========================================
           SEARCH CARD
        ========================================== */

        .uw-liquid-page
        .uw-search-card {
          position: relative;

          z-index: 100;

          overflow:
            visible !important;
        }

        .uw-liquid-page
        .uw-search-card
        .card-body {
          overflow:
            visible !important;
        }

        .uw-liquid-page
        .clienti-dropdown {
          z-index:
            99999 !important;
        }

        /* ==========================================
           EMBEDDED NELL'ANAGRAFICA
           NESSUN SECONDO SCROLL
        ========================================== */

        .uw-backorder-embedded {
          height:
            auto !important;

          min-height:
            0 !important;

          overflow:
            visible !important;

          background:
            transparent !important;
        }

        .uw-backorder-embedded
        .uw-backorder-content {
          width: 100%;

          height:
            auto !important;

          max-height:
            none !important;

          min-height:
            0 !important;

          overflow:
            visible !important;

          padding:
            0 !important;

          margin:
            0 !important;
        }

        .uw-backorder-embedded
        .uw-page-scroll {
          height:
            auto !important;

          max-height:
            none !important;

          min-height:
            0 !important;

          overflow:
            visible !important;
        }

        /* ==========================================
           MOBILE
        ========================================== */

        @media (max-width: 767.98px) {
          .uw-liquid-page .uw-card {
            border-radius:
              18px !important;
          }

          .uw-liquid-page .bo-kpi {
            min-height:
              100px;
          }

          .bo-summary-item {
            border-bottom:
              1px solid
              rgba(255,255,255,.06);
          }
        }
      `}</style>
    </div>
  );
};

export default BackOrder;